import type { ContactDetails, SubjectSelection, TutorMatchResponse, YearLevel } from "../types";

const PAIRING_API_URL = import.meta.env.VITE_PAIRING_API_URL || "";
const PAIRING_API_KEY = import.meta.env.VITE_PAIRING_API_KEY || "";

function yearLevelLabel(year: YearLevel): string {
  switch (year) {
    case "1_6": return "Years 1-6";
    case "7_10": return "Years 7-10";
    case "11_12": return "Years 11-12";
  }
}

const CURRICULUM_OPTIONS = [
  "VCE",
  "HSC",
  "QCE",
  "WACE",
  "SACE",
  "NTCET",
  "TCE",
  "ACT-SSC",
  "IB",
  "KIS",
] as const;

type CurriculumOption = (typeof CURRICULUM_OPTIONS)[number];

function toApiCurriculum(curriculum: string): CurriculumOption {
  const lower = curriculum.toLowerCase();
  if (lower === "primary" || lower === "secondary" || lower === "general") {
    return "KIS";
  }
  const upper = curriculum.toUpperCase();
  if ((CURRICULUM_OPTIONS as readonly string[]).includes(upper)) {
    return upper as CurriculumOption;
  }
  return "KIS";
}

function toApiSubjects(subjectCodes: string[], apiCurriculum: CurriculumOption): string[] {
  return subjectCodes
    .map((s) => {
      if (!s) return null;
      if (s.includes("-")) return s.toUpperCase();
      return `${apiCurriculum}-${s.toUpperCase()}`;
    })
    .filter((s): s is string => s != null && s.length > 0);
}

interface MatchParams {
  year: YearLevel;
  curriculum: string;
  subject: SubjectSelection;
  contact?: ContactDetails;
}

async function callMatch(endpoint: string, params: MatchParams): Promise<TutorMatchResponse> {
  const apiCurriculum = toApiCurriculum(params.curriculum);
  const body: Record<string, unknown> = {
    yearLevel: yearLevelLabel(params.year),
    curriculum: apiCurriculum,
    subjects: toApiSubjects([params.subject.code], apiCurriculum),
    source: "WAY_MATCH_CHATBOT",
    sourceRef: window.location.href,
  };

  // API requires customerEmail, customerName, customerPhone always
  if (params.contact) {
    body.customerEmail = params.contact.email;
    body.customerName = params.contact.fullName;
    body.customerPhone = params.contact.phone || "";
    if (params.contact.message) body.message = params.contact.message;
  } else {
    body.customerEmail = "anonymous@kisacademics.com";
    body.customerName = "Anonymous";
    body.customerPhone = "";
  }

  const base = PAIRING_API_URL.replace(/\/$/, "");
  const res = await fetch(`${base}/public/${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": PAIRING_API_KEY,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API error ${res.status}: ${text}`);
  }

  return res.json();
}

export async function tutorMatchWithApproval(params: MatchParams): Promise<TutorMatchResponse> {
  return callMatch("leads/capture", params);
}
